/**
 * AffectedCropsField — AK-257 «Поражаемые культуры» (section 2) для редактора
 * болезни/вредителя/сорняка. Мульти-выбор культур (chips) + Q3 confirm при
 * снятии культуры, у которой есть привязанные фото.
 *
 * Двухшаговость (Q2): write-path активен только для уже сохранённой сущности
 * (нужен entityId). При create без id поле disabled с подсказкой.
 *
 * Q3: PUT /crops без force возвращает 409 {code:"crop_has_images"}; ловим →
 * показываем confirm-диалог → повтор с force=true (D1: осиротевшие фото удалятся
 * на сервере).
 */
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { MultiSelectChips } from '@/components/reference/MultiSelectChips';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { useCrops } from '@/pages/platform-admin/reference/hooks/useReferenceQueries';
import {
  useAffectedCrops,
  useReplaceAffectedCrops,
} from '@/pages/platform-admin/reference/hooks/useImageBankQueries';
import type {
  AffectedCrop,
  ImageDict,
} from '@/pages/platform-admin/reference/types';

export interface AffectedCropsFieldProps {
  dict: ImageDict;
  /** null while the entity is unsaved (create step 1 not committed). */
  entityId: string | null;
  /** Bubble up the current affected list so the parent can feed PhotoBank. */
  onCropsChange?: (crops: AffectedCrop[]) => void;
}

interface PendingRemove {
  cropId: string;
  cropName: string;
  imageCount: number;
  nextCropIds: string[];
}

export function AffectedCropsField({
  dict,
  entityId,
  onCropsChange,
}: AffectedCropsFieldProps) {
  const cropsListQ = useCrops({ page: 1, page_size: 500 });
  const affectedQ = useAffectedCrops(dict, entityId);
  const replaceMut = useReplaceAffectedCrops(dict);

  const [pending, setPending] = useState<PendingRemove | null>(null);

  // AK-257 fix B-1 — bubble up the loaded affected list so the parent drawer can
  // gate/feed the PhotoBank on edit-open, not only after a manual mutation.
  // `affectedQ.data` identity is stable across refetches (TanStack returns the
  // same array reference when data is unchanged), and `onCropsChange` is a
  // setState dispatcher (stable identity) — so this effect fires once per real
  // data change, no render loop.
  useEffect(() => {
    if (affectedQ.data) onCropsChange?.(affectedQ.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affectedQ.data]);

  const options =
    cropsListQ.data?.items.map((c) => ({
      value: c.id,
      label: c.name_translations.ru ?? c.name_translations.en ?? c.id,
    })) ?? [];

  const affected = affectedQ.data ?? [];
  const selected = affected.map((c) => c.crop_id);
  const nameOf = (id: string) =>
    affected.find((c) => c.crop_id === id)?.name ??
    options.find((o) => o.value === id)?.label ??
    '—';

  if (!entityId) {
    return (
      <div className="rounded-lg border-2 border-dashed border-border bg-secondary/40 px-4 py-5 text-center">
        <p className="text-sm font-semibold text-accent-brand-strong">
          Сначала сохраните запись
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Поражаемые культуры и фото станут доступны после сохранения (нужен ID).
        </p>
      </div>
    );
  }

  if (affectedQ.isLoading || cropsListQ.isLoading) {
    return <Skeleton className="h-10 w-full" />;
  }

  const applyReplace = async (cropIds: string[], force: boolean) => {
    return replaceMut.mutateAsync({
      entityId,
      crops: cropIds.map((id) => ({ crop_id: id })),
      force,
    });
  };

  const handleChange = async (next: string[]) => {
    const removed = selected.filter((id) => !next.includes(id));
    try {
      const result = await applyReplace(next, false);
      onCropsChange?.(result);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const raw = err.rawDetail as
          | { code?: string; crop_id?: string; image_count?: number }
          | undefined;
        if (raw?.code === 'crop_has_images') {
          const cid = raw.crop_id ?? removed[0];
          setPending({
            cropId: cid,
            cropName: nameOf(cid),
            imageCount: raw.image_count ?? 0,
            nextCropIds: next,
          });
          return;
        }
      }
      toast.error(err instanceof ApiError ? err.detail : 'Не удалось сохранить культуры');
    }
  };

  const confirmRemove = async () => {
    if (!pending) return;
    try {
      const result = await applyReplace(pending.nextCropIds, true);
      onCropsChange?.(result);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.detail : 'Не удалось снять культуру');
    } finally {
      setPending(null);
    }
  };

  return (
    <>
      <MultiSelectChips
        options={options}
        selected={selected}
        onChange={(next) => void handleChange(next)}
        placeholder="Выберите культуры, которые поражает болезнь…"
        disabled={replaceMut.isPending}
      />
      <p className="mt-1.5 text-xs text-muted-foreground">
        Сначала задайте список культур — потом под них можно привязывать фото.
      </p>

      <Dialog open={Boolean(pending)} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="max-w-[430px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-destructive/[0.12] text-destructive">
                <AlertTriangle className="h-4 w-4" />
              </span>
              Снять «{pending?.cropName}»?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            К этой культуре привязано <b className="text-foreground">{pending?.imageCount} фото</b>.
            Фото, общие с другими культурами, останутся в пуле (потеряют только эту
            культуру). Фото, у которых эта культура была единственной, будут{' '}
            <b className="text-foreground">удалены</b> (D1: фото без культур не хранятся).
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)} disabled={replaceMut.isPending}>
              Отмена
            </Button>
            <Button
              variant="destructive"
              onClick={() => void confirmRemove()}
              disabled={replaceMut.isPending}
            >
              Снять культуру
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AffectedCropsField;
